/**
 * D454 step 3: the sheet the map's ••• button opens, "The farm" in the theme's word. Three
 * tiles: Arrange (move fields), Decorate and Photo. Arrange is locked before the generation
 * `arrange.moveFrom` names, and says when it opens. Decorate and Photo are what the button
 * opened before (D331, D292, D442).
 */
import type { JSX } from 'preact';
import { idleArrangeOpen } from '@hedgerow/sim';
import { Icon } from '../ui/Icon.tsx';
import { SheetClose } from '../ui/Sheet.tsx';
import { arrangeLockedText } from './arrange-view.ts';
import { idleContent, useIdle } from './store.ts';
import { t as term } from './terms.ts';

interface TileProps {
  id: string;
  icon: string;
  label: string;
  note: string;
  locked?: boolean;
  onPress: () => void;
}

function Tile({ id, icon, label, note, locked, onPress }: TileProps) {
  const style: JSX.CSSProperties = {
    display: 'grid',
    gap: 6,
    justifyItems: 'center',
    alignContent: 'center',
    minHeight: 84,
    padding: 8,
    textAlign: 'center',
    borderRadius: 'var(--radius-md)',
    background: 'var(--surface-2)',
    border: '1px solid var(--line)',
    color: 'var(--ink)',
    cursor: 'pointer',
  };
  return (
    <button
      type="button"
      data-testid={id}
      data-locked={locked ? 'true' : undefined}
      onClick={locked ? undefined : onPress}
      style={style}
    >
      <span style={{ color: locked ? 'var(--ink-2)' : 'var(--accent)', display: 'flex' }}>
        <Icon name={icon} size={22} />
      </span>
      <span style={{ font: '600 var(--fs-body)/1.25 var(--font-ui)' }}>{label}</span>
      <span style={{ font: '400 var(--fs-caption)/1.35 var(--font-ui)', color: 'var(--ink)' }}>
        {note}
      </span>
    </button>
  );
}

export function FarmSheet() {
  const world = useIdle((s) => s.world);
  if (!world) return null;
  const open = idleArrangeOpen(world, idleContent);
  const close = () => useIdle.getState().setFarmOpen(false);
  return (
    <>
      <div
        data-testid="farm-scrim"
        onClick={close}
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, zIndex: 39, background: 'var(--juice-scrim)' }}
      />
      <div
        data-testid="farm-sheet"
        style={{
          // Over everything, as the Decorations sheet is: the map above stays in view.
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 40,
          boxSizing: 'border-box',
          display: 'grid',
          gap: 10,
          padding: '8px var(--edge) calc(12px + env(safe-area-inset-bottom))',
          borderRadius: '12px 12px 0 0',
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.12)',
          background: 'var(--bg)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: 'var(--line)',
            margin: '0 auto',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 28 }}>
          <h2 style={{ margin: 0, flex: 1, font: '600 var(--fs-title)/1.3 var(--font-ui)' }}>
            The {term('world')}
          </h2>
          <span data-testid="farm-close" style={{ display: 'flex' }}>
            <SheetClose onClose={close} />
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
          <Tile
            id="farm-arrange"
            icon="move"
            label="Arrange"
            note={
              open ? `Move ${idleContent.arrange.places?.crop ?? 'plot'}s` : arrangeLockedText()
            }
            locked={!open}
            onPress={() => {
              useIdle.getState().enterArrange();
            }}
          />
          <Tile
            id="farm-decorate"
            icon="sparkles"
            label="Decorate"
            note="Change the look"
            onPress={() => {
              close();
              useIdle.getState().setDecorOpen(true);
            }}
          />
          <Tile
            id="farm-photo"
            icon="camera"
            label="Photo"
            note={`Picture of your ${term('world')}`}
            onPress={() => {
              close();
              useIdle.getState().enterPhotoMode();
            }}
          />
        </div>
      </div>
    </>
  );
}
